import OptionSwitcher from "components/OptionSwitcher";
import ButtonHelpTooltip from "components/Tooltip/ButtonHelpTooltip";
import CardHelpTooltip from "components/Tooltip/CardHelpTooltip";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { CommonGrid } from "styledComponents/common";

import dashboardStatIcon from "../../../assets/img/icons/dashboard-stat-icon.svg";
import { currencySymbolMap } from "../../../utils/currencies";
import { getHoursWithSeconds, toHHMMSS } from "../../../utils/helper";
import {
  DashBoardCommonGrid,
  DashboardContainerWithToggleButton,
} from "../../DashBoardNew/dashBoardNewStyles";
import DashboardStatCard from "./DashboardStatCard";

const DashBoardFirstSection = ({
  history,
  selectedOrganization,
  getDashboardProjectCount,
  getDashboardOperationTopSection,
  projectCountIsLoading,
  projectCountData,
  getDashboardTimesheetCount,
  timesheetCountIsLoading,
  timesheetCountData,
  operationTopSectionIsLoading,
  operationTopSectionData,
  serviceTopSectionIsLoading,
  serviceTopSectionData,
  getDashboardServiceTopSection,
}) => {
  const [revenueData, setRevenueData] = useState(0);
  const [dueData, setDueData] = useState(0);
  const [invoiceCountData, setInvoiceCountData] = useState(0);
  const [invoicePendingData, setInvoicePendingData] = useState(0);
  const [revenueInvoiceCountData, setRevenueInvoiceCountData] = useState(0);
  const [dueInvoiceCountData, setDueInvoiceCountData] = useState(0);
  const [geofenceAttendanceData, setGeofenceAttendanceData] = useState(0);
  const [totalProjectsData, setTotalProjectsData] = useState(0);
  const [totalTimeSheetSubmittedData, setTotalTimeSheetSubmittedData] =
    useState(0);
  const [totalTimeIn, setTotalTimeIn] = useState("0s");
  const weekDayMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  const weekStartDay = (
    localStorage.getItem("start_day_of_week") || "monday"
  ).toLowerCase();
  const rangeOptions = [
    { label: "This week", value: "week" },
    { label: "This month", value: "month" },
    { label: "Today", value: "today" },
  ];
  const [selectedRangeOption, setSelectedRangeOption] = useState(
    rangeOptions[0],
  );
  const currency = localStorage.getItem("currency") || "USD";
  const currencySymbol = currencySymbolMap[currency] || "$";
  const orgType = selectedOrganization?.type || "operation";
  const isServiceType = orgType === "service";
  const isOperationType = orgType === "operation";

  const getRangeTitle = rangeValue => {
    if (rangeValue === "today") return "Today";
    if (rangeValue === "month") return "Monthly";
    return "Weekly";
  };

  const selectedTopSectionData = isOperationType
    ? operationTopSectionData
    : serviceTopSectionData;
  const selectedTopSectionIsLoading = isOperationType
    ? operationTopSectionIsLoading
    : serviceTopSectionIsLoading;
  const selectedRangeTitle = getRangeTitle(
    selectedRangeOption?.value || "week",
  );
  const timezone = selectedOrganization?.timezone || moment.tz.guess();

  const formatOperationWorkingTime = data => {
    const totalWorkingSeconds = Number(data?.total_working_seconds);

    if (Number.isFinite(totalWorkingSeconds) && totalWorkingSeconds >= 0) {
      return getHoursWithSeconds(toHHMMSS(totalWorkingSeconds));
    }

    const totalWorkingHours = Number(data?.total_working_hours);

    if (Number.isFinite(totalWorkingHours) && totalWorkingHours >= 0) {
      return getHoursWithSeconds(
        toHHMMSS(Math.round(totalWorkingHours * 3600)),
      );
    }

    const fallbackSeconds = Number(timesheetCountData?.total_time_seconds);

    if (Number.isFinite(fallbackSeconds) && fallbackSeconds >= 0) {
      return getHoursWithSeconds(toHHMMSS(fallbackSeconds));
    }

    return "0s";
  };

  const operationTopSectionTotalJobs = selectedTopSectionData?.total_jobs ?? 0;
  const operationTopSectionCompletedJobs =
    selectedTopSectionData?.completed_jobs ?? 0;
  const operationTopSectionCheckedInJobs =
    selectedTopSectionData?.jobs_with_checkin_or_geofence_clockin ?? 0;
  const operationTopSectionLateCheckins =
    selectedTopSectionData?.late_checkin_jobs ?? 0;
  const operationTopSectionUnscheduledJobs =
    selectedTopSectionData?.unscheduled_jobs ?? 0;
  const operationTopSectionSubmittedJobs =
    selectedTopSectionData?.submitted_jobs ?? 0;
  const operationTopSectionApprovedJobs =
    selectedTopSectionData?.approved_jobs ?? 0;
  const operationTopSectionActiveProjects =
    selectedTopSectionData?.total_active_projects ??
    projectCountData?.total_projects ??
    0;
  const operationTopSectionWorkingTime = formatOperationWorkingTime(
    selectedTopSectionData,
  );

  const getRangeDates = rangeValue => {
    const now = moment.tz(timezone);
    const end_time = now.format("YYYY-MM-DD");

    if (rangeValue === "today") {
      return {
        start_time: end_time,
        end_time,
      };
    }

    if (rangeValue === "month") {
      return {
        start_time: now.clone().startOf("month").format("YYYY-MM-DD"),
        end_time,
      };
    }

    const selectedDayIndex = weekDayMap[weekStartDay] ?? weekDayMap.monday;
    const currentDayIndex = now.day();
    const diff = (currentDayIndex - selectedDayIndex + 7) % 7;

    return {
      start_time: now.clone().subtract(diff, "days").format("YYYY-MM-DD"),
      end_time,
    };
  };

  //call the api to get the data
  useEffect(() => {
    if (!selectedOrganization?.id) {
      return;
    }

    const payload = {
      organization_id: selectedOrganization.id,
    };

    if (isServiceType && getDashboardServiceTopSection) {
      const { start_time, end_time } = getRangeDates(
        selectedRangeOption?.value || "week",
      );

      getDashboardServiceTopSection({
        organization_id: selectedOrganization.id,
        start_time,
        end_time,
      });
    }

    if (isOperationType && getDashboardOperationTopSection) {
      const { start_time, end_time } = getRangeDates(
        selectedRangeOption?.value || "week",
      );

      getDashboardOperationTopSection({
        organization_id: selectedOrganization.id,
        start_time,
        end_time,
      });
    }

    // Call project count API only for operation type organizations
    // if (isOperationType && getDashboardProjectCount) {
    //   getDashboardProjectCount(payload);
    // }

    // Call timesheet count API only for operation type organizations
    // if (isOperationType && getDashboardTimesheetCount) {
    //   getDashboardTimesheetCount(payload);
    // }
  }, [
    selectedOrganization,
    selectedRangeOption,
    // getDashboardProjectCount,
    // getDashboardOperationTopSection,
    // getDashboardServiceTopSection,
    // getDashboardTimesheetCount,
    // timezone,
  ]);

  // Format currency
  const formatCurrency = (value, currencyCode = currency) => {
    if (value === null || value === undefined) {
      return `${currencySymbol}0`;
    }

    try {
      // Try to use Intl.NumberFormat with the currency code
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
      return formatted;
    } catch (error) {
      // Fallback: manually format with symbol if Intl doesn't support the currency
      const formattedValue = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
      return `${
        currencySymbolMap[currencyCode] || currencySymbol
      }${formattedValue}`;
    }
  };

  useEffect(() => {
    if (
      !selectedTopSectionData ||
      !Object.keys(selectedTopSectionData).length
    ) {
      return;
    }

    const serviceCurrency = selectedTopSectionData?.currency || currency;
    const revenueAmount =
      selectedTopSectionData?.revenue?.amount ??
      selectedTopSectionData?.revenue?.revenue ??
      selectedTopSectionData?.revenue ??
      0;
    const revenueInvoiceCount =
      selectedTopSectionData?.revenue?.revenue_invoice_count ??
      selectedTopSectionData?.revenue_invoice_count ??
      0;
    const dueAmount =
      selectedTopSectionData?.invoice_due?.amount ??
      selectedTopSectionData?.invoice_due?.due ??
      selectedTopSectionData?.due ??
      0;
    const dueInvoiceCount =
      selectedTopSectionData?.invoice_due?.due_invoice_count ??
      selectedTopSectionData?.due_invoice_count ??
      0;
    const geofenceAttendanceCount =
      selectedTopSectionData?.job_count
        ?.jobs_with_checkin_or_geofence_clockin ??
      selectedTopSectionData?.jobs_with_checkin_or_geofence_clockin ??
      0;
    const invoiceRaisedCount =
      selectedTopSectionData?.invoice?.invoice_raised_count ??
      selectedTopSectionData?.invoice_raised_count ??
      selectedTopSectionData?.invoice_count ??
      0;
    const pendingInvoiceCount =
      selectedTopSectionData?.invoice?.invoice_pending ??
      selectedTopSectionData?.invoice_pending ??
      0;

    setRevenueData(
      formatCurrency(Math.abs(revenueAmount || 0), serviceCurrency),
    );
    setDueData(formatCurrency(Math.abs(dueAmount || 0), serviceCurrency));
    setInvoiceCountData(Math.abs(invoiceRaisedCount || 0));
    setInvoicePendingData(Math.abs(pendingInvoiceCount || 0));
    setRevenueInvoiceCountData(Math.abs(revenueInvoiceCount || 0));
    setDueInvoiceCountData(Math.abs(dueInvoiceCount || 0));
    setGeofenceAttendanceData(Math.abs(geofenceAttendanceCount || 0));
  }, [selectedTopSectionData, currency, currencySymbol]);

  // Set project count data from API response
  useEffect(() => {
    if (!isOperationType) {
      return;
    }

    if (projectCountData && Object.keys(projectCountData).length) {
      setTotalProjectsData(projectCountData.total_projects || 0);
    } else {
      setTotalProjectsData(0);
    }
  }, [projectCountData, isOperationType]);

  // Convert seconds to HH:MM:SS format for helper function
  const convertSecondsToTimeString = seconds => {
    if (!seconds || seconds === 0) return "0:0:0";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes}:${secs}`;
  };

  // Set timesheet count data from API response
  useEffect(() => {
    if (!isOperationType) {
      return;
    }

    if (timesheetCountData && Object.keys(timesheetCountData).length) {
      const totalTimesheets = timesheetCountData.total_timesheets || 0;
      const totalTimeSeconds = timesheetCountData.total_time_seconds || 0;

      // Convert seconds to time format and format using helper function
      const timeString = convertSecondsToTimeString(totalTimeSeconds);
      const formattedTime = getHoursWithSeconds(timeString);

      // Display total timesheets with formatted time as secondary text or combine them
      // Based on requirement, showing total_timesheets as value
      setTotalTimeSheetSubmittedData(totalTimesheets);
      setTotalTimeIn(formattedTime);
    } else {
      setTotalTimeSheetSubmittedData(0);
      setTotalTimeIn("0s");
    }
  }, [timesheetCountData, isOperationType]);

  return (
    <DashboardContainerWithToggleButton>
      <OptionSwitcher
        options={rangeOptions}
        value={selectedRangeOption.value}
        onChange={option => setSelectedRangeOption(option)}
        width="32px"
        height="100%"
      />

      <DashBoardCommonGrid
        responsiveBreakpoint="1244px"
        responsiveColumns="1fr"
        columns="1fr 1fr"
        gap="20px"
        padding="12px 0px"
      >
        {/* first column */}
        <>
          <CommonGrid
            responsiveColumns="1fr"
            responsiveBreakpoint="533px"
            disableResponsive={true}
            columns="1fr 1fr"
            gap="20px"
          >
            {/* job Section both for operation and service */}
            <ButtonHelpTooltip
              transform="translateX(-50%) translateY(45%)"
              enabled={true}
              text={`The total number of ${
                isOperationType ? "tasks" : "jobs"
              } booked in the selected range.`}
            >
              <DashboardStatCard
                title={`${
                  isOperationType ? "Tasks" : "Jobs"
                } ${selectedRangeTitle}`}
                value={
                  isOperationType
                    ? operationTopSectionTotalJobs
                    : selectedTopSectionData?.job_count?.total_jobs || 0
                }
                secondaryText={
                  isOperationType
                    ? `${operationTopSectionCheckedInJobs} visited`
                    : `${geofenceAttendanceData} visited`
                }
                icon={dashboardStatIcon}
                handleIconClick={() =>
                  isOperationType
                    ? history.push(`/user/tasks`)
                    : history.push(`/user/jobs`)
                }
                isLoading={selectedTopSectionIsLoading}
              />
            </ButtonHelpTooltip>

            {/* Revenue only for service type */}
            {!isOperationType && (
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="Revenue accumulated in the selected range."
              >
                <DashboardStatCard
                  title={`Revenue ${selectedRangeTitle}`}
                  value={revenueData}
                  secondaryText={`on ${revenueInvoiceCountData} invoices`}
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/invoices`)}
                  isLoading={serviceTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
            )}
            {/* total projects today */}
            {/* {isOperationType && <ButtonHelpTooltip transform='translateX(-50%) translateY(45%)'  enabled = {true} text = 'The total number of projects created in the selected range.'>
          <DashboardStatCard
          title={`Total Projects Created ${selectedRangeTitle}`}
          value={totalProjectsData}
          secondaryText=""
          icon={dashboardStatIcon}
          handleIconClick={() => history.push(`/user/project`)}
          isLoading={projectCountIsLoading}
        />
        </ButtonHelpTooltip>} */}
            {/* unscheduled task section only for operation type */}
            {isOperationType && (
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="The total number of unscheduled tasks created till now"
              >
                <DashboardStatCard
                  title={`Unscheduled tasks`}
                  value={operationTopSectionUnscheduledJobs}
                  secondaryText="Till now"
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/tasks`)}
                  isLoading={selectedTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
            )}
          </CommonGrid>
          <CommonGrid
            responsiveColumns="1fr"
            responsiveBreakpoint="533px"
            disableResponsive={true}
            columns="1fr 1fr"
            gap="20px"
          >
            {/* total time sheet submitted today */}
            {/* {isOperationType && <ButtonHelpTooltip transform='translateX(-50%) translateY(45%)'  enabled = {true} text = 'The total time sheets submitted in the selected range.'>
          <DashboardStatCard
          title={`Total Time Sheet Submitted ${selectedRangeTitle}`}
          value={totalTimeIn}
          secondaryText={`${totalTimeSheetSubmittedData} submitted`}
          icon={dashboardStatIcon}
          handleIconClick={() => history.push(`/user/timesheet/daily`)}
          isLoading={timesheetCountIsLoading}
        />
        </ButtonHelpTooltip>} */}
            {/* tasks completed operation type */}
            {isOperationType && (
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="The total number of tasks completed in the selected range."
              >
                <DashboardStatCard
                  title={`Tasks completed ${selectedRangeTitle}`}
                  value={operationTopSectionCompletedJobs}
                  secondaryText=""
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/tasks`)}
                  isLoading={selectedTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
            )}

            {/* task started after start time operation type   */}
            {isOperationType && (
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="The total number of tasks started after the scheduled start time in the selected range."
              >
                <DashboardStatCard
                  title={`Late check-ins ${selectedRangeTitle}`}
                  value={operationTopSectionLateCheckins}
                  secondaryText=""
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/tasks`)}
                  isLoading={selectedTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
            )}

            {/* due invoice only service   */}
            {!isOperationType && (
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="Amount due in the selected range."
              >
                <DashboardStatCard
                  title={`Due ${selectedRangeTitle}`}
                  value={dueData}
                  secondaryText={`on ${dueInvoiceCountData} invoices`}
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/invoices`)}
                  isLoading={serviceTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
            )}
            {/* Invoice Raised Today only for service type */}
            {!isOperationType && (
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="Number of invoices raised(sent) in the selected range."
              >
                <DashboardStatCard
                  title={`Invoice Raised ${selectedRangeTitle}`}
                  value={invoiceCountData}
                  secondaryText={`${invoicePendingData} pending`}
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/invoices`)}
                  isLoading={serviceTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
            )}
          </CommonGrid>
        </>
        {/* 2nd column totally for operation type */}
        {isOperationType && (
          <>
            <CommonGrid
              responsiveColumns="1fr"
              responsiveBreakpoint="533px"
              disableResponsive={true}
              columns="1fr 1fr"
              gap="20px"
            >
              {/* submitted task Section*/}
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text={`The total number of tasks submitted in the selected range.`}
              >
                <DashboardStatCard
                  title={`Submitted task ${selectedRangeTitle}`}
                  value={operationTopSectionSubmittedJobs}
                  secondaryText=""
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/tasks`)}
                  isLoading={selectedTopSectionIsLoading}
                />
              </ButtonHelpTooltip>

              {/* Approved task */}
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="Approved tasks in the selected range."
              >
                <DashboardStatCard
                  title={`Approved task ${selectedRangeTitle}`}
                  value={operationTopSectionApprovedJobs}
                  secondaryText=""
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/task-approval`)}
                  isLoading={selectedTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
            </CommonGrid>
            <CommonGrid
              responsiveColumns="1fr"
              responsiveBreakpoint="533px"
              disableResponsive={true}
              columns="1fr 1fr"
              gap="20px"
            >
              {/* total projects today */}
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="Projects that are currently active (not completed)"
              >
                <DashboardStatCard
                  title={`Total active projects ${selectedRangeTitle}`}
                  value={operationTopSectionActiveProjects}
                  secondaryText="Till Now"
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/project`)}
                  isLoading={selectedTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
              {/* Work hours */}
              <ButtonHelpTooltip
                transform="translateX(-50%) translateY(45%)"
                enabled={true}
                text="The total time employees have worked in the selected range."
              >
                <DashboardStatCard
                  title={`Working time ${selectedRangeTitle}`}
                  value={operationTopSectionWorkingTime}
                  secondaryText=""
                  icon={dashboardStatIcon}
                  handleIconClick={() => history.push(`/user/tasks`)}
                  isLoading={selectedTopSectionIsLoading}
                />
              </ButtonHelpTooltip>
            </CommonGrid>
          </>
        )}
      </DashBoardCommonGrid>
    </DashboardContainerWithToggleButton>
  );
};

export default DashBoardFirstSection;
